import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuthStore } from '@shared/state';
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, getAuth } from 'firebase/auth';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { app } from '@shared/services/firebase';
import { Shield, Eye, EyeOff } from 'lucide-react';
import { Header } from '../../components/ui/Header';

const googleProvider = new GoogleAuthProvider();

export function AdminLogin() {
  const user = useAuthStore((s) => s.user);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // Already logged in and admin — go straight to dashboard
  if (user && (user as { role?: string }).role === 'admin') {
    return <Navigate to="/admin-dashboard" replace />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) {
      setError('Email and password required');
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const auth = getAuth(app);
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const uid = credential.user.uid;

      const userSnap = await getDoc(doc(getFirestore(app), 'users', uid));
      const role = userSnap.data()?.role as string | undefined;

      if (role !== 'admin') {
        await auth.signOut();
        setError('Access denied. Admin credentials required.');
        setSubmitting(false);
        return;
      }

      navigate('/admin-dashboard');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      if (message.includes('invalid-credential') || message.includes('wrong-password')) {
        setError('Invalid email or password.');
      } else if (message.includes('user-not-found')) {
        setError('No account found with this email.');
      } else {
        setError(message);
      }
      setSubmitting(false);
    }
  }

  async function onGoogleSignIn() {
    setSubmitting(true);
    setError(null);
    try {
      const auth = getAuth(app);
      const credential = await signInWithPopup(auth, googleProvider);
      const uid = credential.user.uid;

      const userSnap = await getDoc(doc(getFirestore(app), 'users', uid));
      const role = userSnap.data()?.role as string | undefined;

      if (role !== 'admin') {
        await auth.signOut();
        setError('Access denied. Admin credentials required.');
        setSubmitting(false);
        return;
      }

      navigate('/admin-dashboard');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Google sign-in failed';
      if (message.includes('popup-closed')) {
        setError(null);
      } else {
        setError(message);
      }
      setSubmitting(false);
    }
  }

  return (
    <div
      className="min-h-screen"
      style={{ background: 'linear-gradient(135deg, #0B0E1A, #111528, #0E1225)' }}
    >
      <Header />
      <div className="flex items-center justify-center px-4 min-h-[calc(100vh-4rem)]">
      <div
        className="w-full max-w-sm rounded-2xl p-7 backdrop-blur-xl"
        style={{
          background: 'linear-gradient(145deg, rgba(20,24,48,0.85), rgba(14,18,37,0.65))',
          border: '1px solid rgba(108,92,231,0.2)',
          boxShadow:
            '0 25px 60px -12px rgba(0,0,0,0.7), ' +
            '0 0 80px -20px rgba(108,92,231,0.15), ' +
            'inset 0 1px 0 rgba(255,255,255,0.06), ' +
            'inset 0 -1px 0 rgba(0,0,0,0.3)',
        }}
      >
        <div className="flex items-center justify-center gap-2 mb-1">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(108,92,231,0.15)', border: '1px solid rgba(108,92,231,0.3)' }}
          >
            <Shield size={18} className="text-indigo-400" />
          </div>
        </div>
        <h1 className="font-heading text-xl font-bold text-center mb-1">Admin Portal</h1>
        <p className="text-content-muted text-xs text-center mb-6">CogniPrint Administration</p>

        {error && (
          <div
            className="rounded-lg text-sm p-3 mb-4 text-center"
            style={{
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.25)',
              color: '#f87171',
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={onSubmit}>
          <label className="block text-[11px] uppercase tracking-wider text-content-muted mb-1">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="hello@cogniprint.io"
            autoComplete="email"
            className="w-full rounded-lg text-sm p-2.5 mb-4 bg-dark-subtle border border-dark-border text-content focus:border-indigo-500 focus:outline-none transition-colors"
          />

          <label className="block text-[11px] uppercase tracking-wider text-content-muted mb-1">
            Password
          </label>
          <div className="relative mb-5">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              autoComplete="current-password"
              className="w-full rounded-lg text-sm p-2.5 bg-dark-subtle border border-dark-border text-content focus:border-indigo-500 focus:outline-none transition-colors pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-content-muted hover:text-content"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg font-semibold text-sm py-2.5 text-white disabled:opacity-50 transition-all"
            style={{ background: 'linear-gradient(135deg, #6C5CE7, #A39BE0)' }}
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Signing in…
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-dark-border" />
          <span className="text-xs text-content-dim">or</span>
          <div className="flex-1 h-px bg-dark-border" />
        </div>

        <button
          onClick={onGoogleSignIn}
          disabled={submitting}
          className="w-full flex items-center justify-center gap-2.5 rounded-lg text-sm font-medium py-2.5 border border-dark-border bg-dark-subtle text-content hover:bg-white/5 disabled:opacity-50 transition-all"
        >
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>
      </div>
      </div>
    </div>
  );
}
